import { AgGridReact } from "ag-grid-react";
import { useMemo, useState, useEffect } from "react";
import { getDocumentsByWebStatus , getDocumentByDocumentId} from "../services/adminDocuments_service";
import RecipientsModal from "./modals/RecipientsModal";

const DocumentsGrid = ({ filters, onSelectionChange, reloadGrid, stopReloadGrid }) => {

  const [rowData, setRowData] = useState([]);
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);

  const handleDocumentDownload = async (row) => {
  try {
    const id = row.documentId;
    const name = (row.documentName || "document.pdf").replace(/\+/g, " ");
    const extension = name.split(".").pop()?.toLowerCase();

    const response = await getDocumentByDocumentId(id, false);

    const contentType =
      extension === "pdf"
        ? "application/pdf"
        : response.headers["content-type"] || "application/octet-stream";

    const blob = new Blob([response.data], { type: contentType });
    const fileURL = URL.createObjectURL(blob);

    if (extension === "pdf") {
      window.open(fileURL, "_blank");
      
    } else {
      const link = document.createElement("a");
      link.href = fileURL;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }

    URL.revokeObjectURL(fileURL);

  } catch (err) {
    console.error("Download failed", err);
  }
};

  const formatDateMMDDYYYY = (date) => {
  if (!date) return "";

  const d = new Date(date);

  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();

  return `${month}/${day}/${year}`;
};
  const fetchGridData = async () => {

  const today = new Date();

  // last day of month 4 months ago
  const fourMonthsAgoLastDay = new Date(
    today.getFullYear(),
    today.getMonth() - 3,
    0
  );

  const categories = filters?.category || "";
  const documentname = filters?.documentName || "";

  const fromdate = filters?.fromDate
    ? formatDateMMDDYYYY(filters.fromDate)
    : formatDateMMDDYYYY(fourMonthsAgoLastDay);

  const todate = filters?.toDate
    ? formatDateMMDDYYYY(filters.toDate)
    : formatDateMMDDYYYY(today);

  const status = filters?.status || "";

  const uploaddate = filters?.uploadedDate
    ? formatDateMMDDYYYY(filters.uploadedDate)
    : null;

  const accountids = filters?.accountids || "";
  const contactids = filters?.contactids || "";

  try {

    const response = await getDocumentsByWebStatus(
      categories,
      documentname,
      fromdate,
      todate,
      uploaddate,
      status,
      contactids,
      accountids
    );

    setRowData(response.data);

  }
  catch {
    setRowData([]);
  }
  stopReloadGrid();

};
  useEffect(()=>{
    fetchGridData();
  },[filters])
  useEffect(()=>{
    if(reloadGrid){
      fetchGridData()
    }
  },[reloadGrid])
  const formatDate = (date) => {
  if (!date) return "";

  const d = new Date(date);

  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();

  return `${month}-${day}-${year}`;
};

const StatusRenderer = (params) => {
  const status = params.data?.documentWebStatusId;

  const handleClick = (e) => {
    e.stopPropagation(); // prevent row selection
    handleDocumentDownload(params.data);
  };

  const baseStyle = {
    fontSize: "20px",
    cursor: "pointer"
  };

  if (status === 1) {
    return (
      <span
        className="far fa-file-pdf"
        title="Published"
        style={{ ...baseStyle, color: "#00FF00" }}
        onClick={handleClick}
      />
    );
  }

  if (status === 2) {
    return (
      <span
        className="far fa-file-pdf"
        title="UnPublished"
        style={{ ...baseStyle, color: "#808080" }}
        onClick={handleClick}
      />
    );
  }

  return (
    <span
      className="far fa-file-pdf"
      title="Deleted"
      style={{ ...baseStyle, color: "#FF0000", position: "relative" }}
      onClick={handleClick}
    >
      <i
        className="fas fa-times"
        style={{
          position: "absolute",
          top: "3px",
          left: "3px",
          fontSize: "21px"
        }}
      />
    </span>
  );
};

const RecipientsRenderer = (params) => {

  const row = params.data;

  if (row.notificationType !== "Manual") {

    return (
      <a
        style={{ cursor: "pointer" }}
        onClick={(e) => {

          e.stopPropagation(); // prevents row selection

          setSelectedDocumentId(row.documentId);
          setShowRecipientsModal(true);

        }}
      >
        Show
      </a>
    );

  }

  return null;

};

const columnDefs = [

  { field: "documentId", hide: true },

  { field: "documentContentId", hide: true },

  {
    headerName: "Status",
    field: "documentWebStatusId",
    width: 60,
    sortable: false,
    cellRenderer: StatusRenderer
  },

  {
    headerName: "Category",
    field: "documentTypeName",
    width: 120
  },

  {
    headerName: "Name",
    field: "documentName",
    flex: 2,
    sortable: false
  },

  {
    headerName: "Type",
    field: "type",
    width: 100,
    sortable: false
  },

  {
    headerName: "Entity",
    field: "entity",
    flex: 1,
    sortable: false
  },

  {
    headerName: "Doc Date",
    field: "documentDate",
    width: 120,
    valueFormatter: p => formatDate(p.value)
  },

  {
    headerName: "Created User",
    field: "createdBy",
    width: 120,
    sortable: false
  },

  {
    headerName: "Uploaded",
    field: "uploaded",
    width: 120,
    sortable: false,
    valueFormatter: p => formatDate(p.value)
  },

  {
    headerName: "Modified",
    field: "lastModified",
    width: 120,
    sortable: false,
    valueFormatter: p => formatDate(p.value)
  },

  {
    headerName: "Notification Type",
    field: "notificationType",
    width: 140,
    sortable: false
  },

  { field: "emailTemplateId", hide: true },

  {
    headerName: "Email Status",
    field: "emailStatus",
    width: 120,
    sortable: false
  },

  {
    headerName: "Recipients",
    field: "recipients",
    width: 120,
    sortable: false,
    cellRenderer: RecipientsRenderer
  },

  {
  headerName: "",
  width: 60,
  checkboxSelection: true,
  suppressMenu: true,
  sortable: false,
  filter: false,
  cellClass: "text-center"
}

]

  return (
    <div
      className="ag-theme-alpine"
      style={{ height: 600, width: "100%", "--ag-header-background-color": "transparent" }}
    >

      <AgGridReact
  theme={"legacy"}
  rowData={rowData}
  columnDefs={columnDefs}
  defaultColDef={{
    tooltipValueGetter: (params) => params.value,
    suppressHeaderMenuButton: true,
    suppressHeaderContextMenu: true,
    suppressHeaderFilterButton: true,
    filter: false
  }}
  rowSelection={{
    mode: "multiRow",
    enableClickSelection: false
  }}
  selectionColumnDef={{ hide: true }}   // hides left auto checkbox column
  pagination={true}
  animateRows={true}
  onRowDoubleClicked={(params) => handleDocumentDownload(params.data)}
  onSelectionChanged={(e) =>
    onSelectionChange(
      e.api.getSelectedRows()
    )
  }
/>
      {showRecipientsModal && (

  <RecipientsModal
    show={showRecipientsModal}
    documentId={selectedDocumentId}
    onClose={() => setShowRecipientsModal(false)}
  />

)}

    </div>
  );
};

export default DocumentsGrid;